import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_API_URL;

const ManageSeo = () => {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isCreated, setIsCreated] = useState(false);

  const [formData, setFormData] = useState({
    // General SEO
    title: "",
    description: "",
    keywords: "",
    canonicalUrl: "",

    // Robots
    robots: {
      index: true,
      follow: true,
    },

    // Open Graph
    ogTitle: "",
    ogDescription: "",
    ogImage: "",

    // Twitter
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",

    // Analytics
    googleAnalyticsId: "",
    googleTagManagerId: "",
    facebookPixelId: "",

    // Others
    favicon: "",
    structuredData: "",
  });

  useEffect(() => {
    fetchSeo();
  }, []);

  const fetchSeo = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/seo`);

      if (res.data.success) {
        setFormData({
          ...formData,
          ...res.data.data,
        });

        setIsCreated(true);
      }
    } catch (err) {
      setIsCreated(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRobotChange = (name) => {
    setFormData((prev) => ({
      ...prev,
      robots: {
        ...prev.robots,
        [name]: !prev.robots[name],
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      let response;

      if (isCreated) {
        response = await axios.put(`${BASE_URL}/seo`, formData);
      } else {
        response = await axios.post(`${BASE_URL}/seo/create`, formData);
      }

      toast.success(response.data.message);

      fetchSeo();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="p-10 text-center text-lg font-semibold">
        Loading SEO Settings...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-8">

      <div className="bg-white rounded-xl shadow-lg p-8">

        <h1 className="text-3xl font-bold mb-8">
          Website SEO Management
        </h1>

        <form onSubmit={handleSubmit}>

          {/* ================= GENERAL SEO ================= */}

          <div className="border rounded-xl p-6 mb-8">

            <h2 className="text-xl font-bold mb-5">
              General SEO
            </h2>

            <div className="grid grid-cols-2 gap-5">

              <div>
                <label>Site Title</label>

                <input
                  className="w-full border rounded-lg p-3 mt-2"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label>Canonical URL</label>

                <input
                  className="w-full border rounded-lg p-3 mt-2"
                  name="canonicalUrl"
                  value={formData.canonicalUrl}
                  onChange={handleChange}
                />
              </div>

            </div>

            <div className="mt-5">

              <label>Description</label>

              <textarea
                rows={4}
                className="w-full border rounded-lg p-3 mt-2"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />

            </div>

            <div className="mt-5">

              <label>Keywords</label>

              <input
                className="w-full border rounded-lg p-3 mt-2"
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
              />

            </div>

            <div className="flex gap-10 mt-5">

              <label className="flex items-center gap-3">

                <input
                  type="checkbox"
                  checked={formData.robots.index}
                  onChange={() => handleRobotChange("index")}
                />

                Index

              </label>

              <label className="flex items-center gap-3">

                <input
                  type="checkbox"
                  checked={formData.robots.follow}
                  onChange={() => handleRobotChange("follow")}
                />

                Follow

              </label>

            </div>

          </div>

          {/* ================= OPEN GRAPH ================= */}

          <div className="border rounded-xl p-6 mb-8">

            <h2 className="text-xl font-bold mb-5">
              Open Graph
            </h2>

            <div className="grid grid-cols-2 gap-5">

              <input
                className="border rounded-lg p-3"
                placeholder="OG Title"
                name="ogTitle"
                value={formData.ogTitle}
                onChange={handleChange}
              />

              <input
                className="border rounded-lg p-3"
                placeholder="OG Image URL"
                name="ogImage"
                value={formData.ogImage}
                onChange={handleChange}
              />

            </div>

            <textarea
              rows={4}
              className="border rounded-lg p-3 mt-5 w-full"
              placeholder="OG Description"
              name="ogDescription"
              value={formData.ogDescription}
              onChange={handleChange}
            />

          </div>

          {/* ================= TWITTER ================= */}

          <div className="border rounded-xl p-6 mb-8">

            <h2 className="text-xl font-bold mb-5">
              Twitter Card
            </h2>

            <div className="grid grid-cols-2 gap-5">

              <input
                className="border rounded-lg p-3"
                placeholder="Twitter Title"
                name="twitterTitle"
                value={formData.twitterTitle}
                onChange={handleChange}
              />

              <input
                className="border rounded-lg p-3"
                placeholder="Twitter Image"
                name="twitterImage"
                value={formData.twitterImage}
                onChange={handleChange}
              />

            </div>

            <textarea
              rows={4}
              className="border rounded-lg p-3 mt-5 w-full"
              placeholder="Twitter Description"
              name="twitterDescription"
              value={formData.twitterDescription}
              onChange={handleChange}
            />

          </div>

          {/* ================= ANALYTICS ================= */}

          <div className="border rounded-xl p-6 mb-8">

            <h2 className="text-xl font-bold mb-5">
              Analytics
            </h2>

            <div className="grid grid-cols-3 gap-5">

              <input
                className="border rounded-lg p-3"
                placeholder="Google Analytics ID"
                name="googleAnalyticsId"
                value={formData.googleAnalyticsId}
                onChange={handleChange}
              />

              <input
                className="border rounded-lg p-3"
                placeholder="Google Tag Manager ID"
                name="googleTagManagerId"
                value={formData.googleTagManagerId}
                onChange={handleChange}
              />

              <input
                className="border rounded-lg p-3"
                placeholder="Facebook Pixel ID"
                name="facebookPixelId"
                value={formData.facebookPixelId}
                onChange={handleChange}
              />

            </div>

          </div>

          {/* ================= FAVICON ================= */}

          <div className="border rounded-xl p-6 mb-8">

            <h2 className="text-xl font-bold mb-5">
              Favicon
            </h2>

            <input
              className="border rounded-lg p-3 w-full"
              placeholder="Favicon URL"
              name="favicon"
              value={formData.favicon}
              onChange={handleChange}
            />

          </div>

          {/* ================= STRUCTURED DATA ================= */}

          <div className="border rounded-xl p-6 mb-8">

            <h2 className="text-xl font-bold mb-5">
              Structured Data
            </h2>

            <textarea
              rows={8}
              className="border rounded-lg p-3 w-full font-mono"
              placeholder="Paste JSON-LD"
              name="structuredData"
              value={formData.structuredData}
              onChange={handleChange}
            />

          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700"
          >
            {loading
              ? "Saving..."
              : isCreated
              ? "Update SEO"
              : "Create SEO"}
          </button>

        </form>

      </div>

    </div>
  );
};

export default ManageSeo;